import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Image as ImageIcon,
  Wand2,
  Loader2,
  Upload,
  Download as DownloadIcon,
  Trash2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { API, BACKEND_URL } from "./utils";

const IMAGE_PROMPT_PRESETS = [
  {
    label: "Mamba im Regen",
    prompt:
      "Close-up of a black 4keeperz Mamba Edition goalkeeper glove with bright green latex palm details, hand catching a wet football, raindrops in the air, dramatic side lighting, blurred urban football pitch in background",
  },
  {
    label: "Panther Studio Shot",
    prompt:
      "Hero product shot of an all-black 4keeperz Panther Edition goalkeeper glove on a black studio backdrop, subtle green rim light, hexagonal palm texture visible, premium high-end look",
  },
  {
    label: "Latex Wet Test",
    prompt:
      "Macro shot of green latex glove palm being splashed with water droplets in slow-motion, frozen droplets mid-air, dark dramatic background, showing the German Giga Grip texture in extreme detail",
  },
  {
    label: "Keeper Action",
    prompt:
      "Atmospheric shot of a young goalkeeper in mid-air diving save wearing 4keeperz gloves, motion blur on background, golden hour stadium lighting, ball just touching glove tips",
  },
  {
    label: "Behind the Scenes",
    prompt:
      "Top-down flat lay of 4keeperz packaging being prepared: black Mamba glove, branded box, tissue paper with green logo, on dark concrete surface, moody industrial light",
  },
  {
    label: "Comparison",
    prompt:
      "Side-by-side product shot comparing 4keeperz Mamba Edition (left, green palm) and Panther Edition (right, all black palm) on dark gradient background, clean studio lighting, premium look",
  },
];

const REFERENCES = [
  { value: "auto", label: "Auto erkennen" },
  { value: "mamba", label: "Mamba Foto" },
  { value: "panther", label: "Panther Foto" },
  { value: "both", label: "Beide" },
  { value: "bag", label: "Bag" },
  { value: "upload", label: "Eigenes Bild" },
  { value: "none", label: "Ohne Referenz" },
];

const ImageStudio = () => {
  const { token } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [format, setFormat] = useState("square_1_1");
  const [reference, setReference] = useState("auto");
  const [uploadB64, setUploadB64] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [history, setHistory] = useState([]);

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/admin/image/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpload = (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Bild zu groß (max 10 MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      const b64 = dataUrl.split(",")[1];
      setUploadB64(b64);
      setUploadPreview(dataUrl);
      setReference("upload");
      toast.success(`${file.name} bereit`);
    };
    reader.readAsDataURL(file);
  };

  const clearUpload = () => {
    setUploadB64(null);
    setUploadPreview(null);
    if (reference === "upload") setReference("auto");
  };

  const generate = async () => {
    if (prompt.trim().length < 10) {
      toast.error("Prompt zu kurz (min. 10 Zeichen)");
      return;
    }
    setGenerating(true);
    try {
      const body = {
        prompt,
        format,
        reference: reference === "upload" && !uploadB64 ? "auto" : reference,
      };
      if (reference === "upload" && uploadB64) {
        body.uploaded_image_b64 = uploadB64;
      }
      const res = await axios.post(`${API}/admin/image/generate`, body, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 90000,
      });
      setHistory((prev) => [res.data, ...prev]);
      toast.success("Bild generiert!");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setGenerating(false);
    }
  };

  const deleteDraft = async (id) => {
    try {
      await axios.delete(`${API}/admin/image/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory((prev) => prev.filter((d) => d.id !== id));
      toast.success("Gelöscht");
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  const downloadImage = (url, filename) => {
    // Accept full URLs, backend-relative paths, and data: URLs
    const fullUrl =
      url.startsWith("http") || url.startsWith("data:")
        ? url
        : `${BACKEND_URL}${url}`;
    const a = document.createElement("a");
    a.href = fullUrl;
    a.download = filename || "4keeperz-image.png";
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-6 mt-6" data-testid="image-studio">
      <div className="flex items-center gap-3 mb-3">
        <ImageIcon className="w-5 h-5 text-[#00A651]" />
        <h2 className="font-condensed uppercase tracking-widest text-sm text-white">
          Image Studio
        </h2>
        <span className="text-[10px] font-mono text-white/40 ml-auto">nano-banana</span>
      </div>
      <p className="text-white/60 text-sm leading-relaxed mb-5">
        Mockup-Bilder mit deinen <strong className="text-[#00A651]">echten</strong>{" "}
        Mamba/Panther-Handschuhen als Referenz. Oder eigenes Bild hochladen → die KI ändert nur
        Hintergrund/Licht/Stimmung.
      </p>

      <div className="mb-4">
        <div className="font-condensed uppercase tracking-widest text-[10px] text-white/50 mb-2">
          Welcher Handschuh? (Referenz)
        </div>
        <div className="flex flex-wrap gap-2">
          {REFERENCES.map((r) => (
            <button
              key={r.value}
              onClick={() => setReference(r.value)}
              data-testid={`image-ref-${r.value}`}
              className={`text-xs font-condensed uppercase tracking-wider border px-3 py-2 transition-colors ${
                reference === r.value
                  ? "bg-[#00A651] text-black border-[#00A651]"
                  : "border-white/15 text-white/70 hover:border-[#00A651] hover:text-white"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {reference === "upload" && (
        <div className="mb-5 border border-dashed border-white/20 bg-black p-5">
          {uploadPreview ? (
            <div className="flex items-start gap-4">
              <img
                src={uploadPreview}
                alt="upload"
                className="w-32 h-32 object-cover border border-white/10"
              />
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm font-condensed uppercase tracking-widest">
                  Eigenes Bild bereit
                </div>
                <div className="text-white/50 text-xs mt-1">
                  Die KI verwendet dieses Bild als Referenz und passt nur Hintergrund/Licht/Stimmung
                  an deinen Prompt an.
                </div>
                <button
                  onClick={clearUpload}
                  className="mt-3 text-[10px] font-condensed uppercase tracking-widest text-red-400 hover:text-red-300"
                  data-testid="image-upload-clear"
                >
                  Entfernen
                </button>
              </div>
            </div>
          ) : (
            <label
              className="flex flex-col items-center justify-center cursor-pointer text-white/50 hover:text-[#00A651] transition-colors"
              data-testid="image-upload-zone"
            >
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleUpload(e.target.files?.[0])}
                data-testid="image-upload-input"
              />
              <Upload className="w-6 h-6 mb-2" />
              <span className="font-condensed uppercase tracking-widest text-xs">
                Klick zum Hochladen
              </span>
              <span className="text-[10px] mt-1">JPG / PNG · max 10 MB</span>
            </label>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        {IMAGE_PROMPT_PRESETS.map((p, i) => (
          <button
            key={i}
            onClick={() => setPrompt(p.prompt)}
            className="text-xs font-condensed uppercase tracking-wider border border-white/15 hover:border-[#00A651] hover:text-[#00A651] text-white/70 px-3 py-2 transition-colors"
            data-testid={`image-preset-${i}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="Beschreibe nur die SCENE (Hintergrund/Licht/Stimmung) — der Handschuh kommt von der Referenz. Z.B.: On a wet football pitch at golden hour, dramatic side lighting, raindrops in air..."
        maxLength={600}
        rows={4}
        data-testid="image-prompt-input"
        className="w-full bg-black border border-white/15 focus:border-[#00A651] outline-none px-4 py-3 text-white text-sm transition-colors mb-3 leading-relaxed"
      />

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-2 bg-black border border-white/15 p-1">
          <button
            onClick={() => setFormat("square_1_1")}
            className={`px-3 py-2 text-xs font-condensed uppercase tracking-widest transition-colors ${
              format === "square_1_1" ? "bg-[#00A651] text-black" : "text-white/60 hover:text-white"
            }`}
            data-testid="format-square"
          >
            1:1 Feed
          </button>
          <button
            onClick={() => setFormat("story_9_16")}
            className={`px-3 py-2 text-xs font-condensed uppercase tracking-widest transition-colors ${
              format === "story_9_16" ? "bg-[#00A651] text-black" : "text-white/60 hover:text-white"
            }`}
            data-testid="format-story"
          >
            9:16 Story / Reel
          </button>
        </div>
        <button
          onClick={generate}
          disabled={
            generating || prompt.trim().length < 10 || (reference === "upload" && !uploadB64)
          }
          data-testid="image-generate-btn"
          className="inline-flex items-center gap-2 bg-[#00A651] hover:bg-[#00C961] text-black disabled:opacity-50 disabled:cursor-not-allowed font-condensed uppercase tracking-widest text-xs px-5 py-2.5 transition-colors"
        >
          {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
          {generating ? "Generiere (15-40 Sek)..." : "Bild erstellen"}
        </button>
        <span className="text-[10px] font-mono text-white/40 ml-auto">{prompt.length}/600</span>
      </div>

      {history.length === 0 ? (
        <div className="border border-dashed border-white/10 p-12 text-center text-white/40 text-sm">
          Noch keine Bilder. Wähle ein Preset oder schreib eigenen Prompt und klick auf "Bild erstellen".
        </div>
      ) : (
        <div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
          data-testid="image-gallery"
        >
          {history.map((d) => (
            <article
              key={d.id}
              className="border border-white/10 bg-black overflow-hidden group relative"
              data-testid={`image-card-${d.id}`}
            >
              {(d.images || []).map((img, i) => (
                <div key={i} className="relative">
                  <img
                    src={
                      img.url && (img.url.startsWith("http") || img.url.startsWith("data:"))
                        ? img.url
                        : `${BACKEND_URL}${img.url}`
                    }
                    alt={d.prompt.slice(0, 80)}
                    className={`w-full ${
                      d.format === "story_9_16" ? "aspect-[9/16]" : "aspect-square"
                    } object-cover`}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-colors flex items-end p-3 opacity-0 group-hover:opacity-100">
                    <p className="text-white text-[10px] leading-tight line-clamp-3">{d.prompt}</p>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between border-t border-white/10 px-2 py-1.5">
                <span className="text-[9px] font-mono text-white/40 uppercase">
                  {d.format === "story_9_16" ? "9:16" : "1:1"}
                  {d.reference && d.reference !== "auto" ? ` · ${d.reference}` : ""}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => downloadImage(d.images[0].url, `4keeperz-${d.id.slice(0, 6)}.png`)}
                    className="p-1.5 hover:text-[#00A651] text-white/60 transition-colors"
                    title="Download"
                    data-testid={`image-download-${d.id}`}
                  >
                    <DownloadIcon className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => deleteDraft(d.id)}
                    className="p-1.5 hover:text-red-400 text-white/60 transition-colors"
                    title="Löschen"
                    data-testid={`image-delete-${d.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageStudio;
